/**
 * Plan de salle v2 (SPEC FC3) : la grille Pick-a-Spot de Mindbody (`PickASpot.roomLayout`) mise en
 * forme pour une grille CSS. Fonctions pures.
 *
 * - La géométrie réelle est respectée (le coach peut être au milieu de la salle, des allées vides
 *   séparent les rangs) ; seules les lignes et colonnes vides des bords sont retirées, et les lignes /
 *   colonnes vides intérieures d'une grille clairsemée (voir `compressAxis`).
 * - Formes de spots = screens/Class/components/ClassRoomLayout.tsx (v1) : banc / sac / double banc
 *   pour les sites Punch (iconIds de la bibliothèque Mindbody), rond partout ailleurs.
 * - États : libre, choisi, invité, pris, à moi (attribut data-state, SPEC § 6.5).
 */
import type { MindbodyPickASpotClassCell, PickaspotClass } from 'types/class';

/** = ClassRoomLayout.tsx : sites Punch (Paris, Marseille). */
export const PUNCH_SITE_IDS = ['5736551', '5745076'];
/** = ClassRoomLayout.tsx : iconIds Mindbody des types de spots (mêmes ids sur tous les plans). */
export const ICON_BENCH = '60d4e39c57443863df72298b';
export const ICON_BAG = '60d4e39c57443863df722985';
export const ICON_DOUBLE_BENCH = '60d4e39c57443863df722995';

export type SpotShape = 'round' | 'bench' | 'bag' | 'double';
export type SpotState = 'free' | 'selected' | 'guest' | 'taken' | 'mine';

export interface PlanCell {
  key: string;
  /** Ligne et colonne dans la grille CSS (base 1, bords vides retirés). */
  row: number;
  col: number;
  colSpan: number;
  kind: 'spot' | 'coach' | 'other';
  spotNumber?: number;
  shape: SpotShape;
  state: SpotState;
  /** Spot réservé par le client pour son invité (réservation Pick-a-Spot de type Guest). */
  isGuestReservation?: boolean;
}

export interface MySpot {
  spotNumber: number;
  isGuest: boolean;
}

export interface PlanModel {
  rows: number;
  /** Colonnes de la grille CSS (en demi-colonnes pour un plan en quinconce : `half`). */
  cols: number;
  /**
   * Plan en quinconce (rangées décalées d'une colonne, aucun spot voisin d'un autre dans sa rangée) : chaque spot
   * s'étend sur 2 colonnes de la grille, qui sont des demi-colonnes du plan Mindbody (voir `isStaggered`).
   */
  half: boolean;
  cells: PlanCell[];
  /** Spots ni pris ni à moi (place libre réelle sur le plan). */
  freeSpotCount: number;
  /** = ClassRoomLayout.tsx `showShapesLegend` : Punch, et le plan mélange bancs et sacs. */
  showShapesLegend: boolean;
  /** = ClassRoomLayout.tsx `doubleBenchSpots`. */
  doubleBenchSpots: number[];
  /** Spots réservés par le client (le premier = le sien, les suivants = invités). */
  mySpots: MySpot[];
}

export interface BuildPlanOptions {
  userId?: string | null;
  selected?: number | null;
  guest?: number | null;
  isPunch: boolean;
  /** = ClassRoomLayout.tsx : cours complet, les spots libres s'affichent pris (non activables). */
  classIsFull: boolean;
}

export function shapeOf(cell: Pick<MindbodyPickASpotClassCell, 'iconId'>, isPunch: boolean): SpotShape {
  if (!isPunch) return 'round';
  if (cell.iconId === ICON_BENCH) return 'bench';
  if (cell.iconId === ICON_BAG) return 'bag';
  if (cell.iconId === ICON_DOUBLE_BENCH) return 'double';
  return 'round';
}

const isEmpty = (cell: MindbodyPickASpotClassCell | undefined) =>
  !cell || cell.cellContent === 'Empty' || (cell.cellContent === 'Spot' && !cell.spot?.spotNumber);

/**
 * Part des lignes (ou colonnes) vides, intérieures, au-delà de laquelle la grille est dite clairsemée et ses lignes
 * vides intérieures retirées. Salles RIISE Reformer : 11 reformers une colonne sur deux (colonnes 2, 4… 22) et une
 * rangée sur deux, soit 9 colonnes vides sur 21 (43 %) — le plan gardait 21 colonnes, spots de 30,7 px au bureau,
 * 504 px de plan pour 332 visibles à 390 (revue du 05/10). Les allées des salles Dynamo restent : Sèvres 1 colonne
 * vide sur 13 (8 %), Lafayette 0 sur 14 ; une rangée vide sur 6 ou 7.
 */
export const SPARSE_EMPTY_SHARE = 1 / 3;

/**
 * Indices (base 1) des lignes ou colonnes gardées entre `min` et `max` : toutes, sauf les vides intérieures d'une
 * grille clairsemée.
 */
function compressAxis(min: number, max: number, occupied: Set<number>): Map<number, number> {
  const span = max - min + 1;
  const emptyCount = span - [...occupied].filter((i) => i >= min && i <= max).length;
  const sparse = span > 2 && emptyCount / span > SPARSE_EMPTY_SHARE;
  const index = new Map<number, number>();
  let next = 1;
  for (let i = min; i <= max; i += 1) {
    if (!sparse || occupied.has(i)) index.set(i, next++);
  }
  return index;
}

/**
 * Plan en quinconce : dans chaque rangée, aucune paire de cases occupées voisines (colonnes gardées), et au moins une
 * rangée de 2 cases ou plus. RIISE Oberkampf : 22 reformers sur 17 colonnes, rangées alternées sur les colonnes paires
 * et impaires ; aucune colonne n'est vide, la compression ne s'applique pas, et la grille gardait 17 colonnes avec une
 * colonne vide entre deux spots (spots au plancher de 20 px, plan de 404 px pour 348 visibles à 390 ; revue du 05/10).
 * En demi-colonnes, chaque spot couvre 2 colonnes de la grille : 9 spots de front au lieu de 17 colonnes.
 */
export function isStaggered(rowsOfCols: number[][]): boolean {
  let wide = false;
  for (const list of rowsOfCols) {
    const sorted = [...list].sort((a, b) => a - b);
    if (sorted.length >= 2) wide = true;
    for (let i = 1; i < sorted.length; i += 1) if (sorted[i] - sorted[i - 1] < 2) return false;
  }
  return wide;
}

export function buildPlan(pick: PickaspotClass, options: BuildPlanOptions): PlanModel {
  const allCells = pick?.roomLayout?.roomLayoutGrid?.cells ?? [];
  const reservations = pick?.reservations ?? [];
  const unavailable = new Set(pick?.spots?.unavailableSpotNumbers ?? []);
  const userId = options.userId ? String(options.userId) : null;

  const byPos = new Map<string, MindbodyPickASpotClassCell>();
  allCells.forEach((cell) => byPos.set(`${cell.row}:${cell.column}`, cell));

  const visible = allCells.filter((cell) => !isEmpty(cell));
  if (!visible.length) {
    return { rows: 0, cols: 0, half: false, cells: [], freeSpotCount: 0, showShapesLegend: false, doubleBenchSpots: [], mySpots: [] };
  }

  const minRow = Math.min(...visible.map((c) => c.row));
  const maxRow = Math.max(...visible.map((c) => c.row));
  const minCol = Math.min(...visible.map((c) => c.column));
  const maxCol = Math.max(...visible.map((c) => c.column));
  const rowIndex = compressAxis(minRow, maxRow, new Set(visible.map((c) => c.row)));
  const colIndex = compressAxis(minCol, maxCol, new Set(visible.map((c) => c.column)));
  const keptCols = [...colIndex.keys()];

  const colsByRow = new Map<number, number[]>();
  visible.forEach((c) => {
    const row = rowIndex.get(c.row) as number;
    colsByRow.set(row, [...(colsByRow.get(row) ?? []), colIndex.get(c.column) as number]);
  });
  const half = isStaggered([...colsByRow.values()]);
  /** Largeur d'une case en colonnes de la grille. */
  const unit = half ? 2 : 1;

  const mySpots: MySpot[] = reservations
    .filter((r) => userId !== null && String(r.memberExternalId) === userId && r.spot?.spotNumber)
    .map((r) => ({ spotNumber: r.spot.spotNumber, isGuest: /guest/i.test(r.reservationType ?? '') }));
  // Le spot du membre d'abord, ceux de ses invités ensuite (ordre Mindbody si le type manque).
  mySpots.sort((a, b) => Number(a.isGuest) - Number(b.isGuest));
  if (mySpots.length > 1 && !mySpots.some((s) => s.isGuest)) {
    mySpots.forEach((s, i) => (s.isGuest = i > 0));
  }
  const mine = new Map(mySpots.map((s) => [s.spotNumber, s]));
  const reserved = new Set(reservations.map((r) => r.spot?.spotNumber).filter(Boolean));

  let freeSpotCount = 0;
  const cells: PlanCell[] = [];
  const sorted = [...visible].sort((a, b) => a.row - b.row || a.column - b.column);

  for (const cell of sorted) {
    const row = rowIndex.get(cell.row) as number;
    const col = colIndex.get(cell.column) as number;
    const key = `${cell.row}:${cell.column}`;

    if (cell.cellContent === 'Instructor') {
      // Bloc du coach (maquette .coachspot) : élargi sur les cases vides voisines (colonnes gardées) s'il y en a des
      // deux côtés, pour que le prénom se lise sans déborder sur un spot. En demi-colonnes, un spot à 2 colonnes du
      // coach couvre la demi-colonne voisine : il faut 2 cases vides de chaque côté.
      const emptyAt = (offset: number) => {
        const kept = keptCols[col - 1 + offset];
        return kept !== undefined && isEmpty(byPos.get(`${cell.row}:${kept}`));
      };
      const freeAround = (n: number) => Array.from({ length: n }, (_, i) => i + 1).every((d) => emptyAt(-d) && emptyAt(d));
      const canWiden = half ? freeAround(2) : freeAround(1);
      cells.push({
        key,
        row,
        col: canWiden ? col - 1 : col,
        colSpan: canWiden ? (half ? 4 : 3) : unit,
        kind: 'coach',
        shape: 'round',
        state: 'taken',
      });
      continue;
    }

    if (cell.cellContent !== 'Spot') {
      cells.push({ key, row, col, colSpan: unit, kind: 'other', shape: 'round', state: 'taken' });
      continue;
    }

    const n = cell.spot.spotNumber;
    const shape = shapeOf(cell, options.isPunch);
    let state: SpotState;
    const my = mine.get(n);
    if (my) state = 'mine';
    else if (reserved.has(n) || unavailable.has(n) || cell.spot.isAvailable === false || options.classIsFull) state = 'taken';
    else if (options.selected === n) state = 'selected';
    else if (options.guest === n) state = 'guest';
    else state = 'free';

    if (state === 'free' || state === 'selected' || state === 'guest') freeSpotCount += 1;
    cells.push({ key, row, col, colSpan: unit, kind: 'spot', spotNumber: n, shape, state, isGuestReservation: my?.isGuest });
  }

  const spotCells = allCells.filter((cell) => cell.cellContent === 'Spot' && cell.spot?.spotNumber);
  const showShapesLegend =
    options.isPunch &&
    spotCells.some((cell) => cell.iconId === ICON_BENCH || cell.iconId === ICON_DOUBLE_BENCH) &&
    spotCells.some((cell) => cell.iconId === ICON_BAG);
  const doubleBenchSpots = options.isPunch
    ? spotCells
        .filter((cell) => cell.iconId === ICON_DOUBLE_BENCH)
        .map((cell) => cell.spot.spotNumber)
        .sort((a, b) => a - b)
    : [];

  return {
    rows: rowIndex.size,
    cols: colIndex.size + (half ? 1 : 0),
    half,
    cells,
    freeSpotCount,
    showShapesLegend,
    doubleBenchSpots,
    mySpots,
  };
}

/** = ClassRoomLayout.tsx `doubleBenchNote`. */
export function doubleBenchNote(spots: number[]): string | null {
  if (spots.length === 1) return `Le spot ${spots[0]} est un double banc, sans accès direct aux sacs.`;
  if (spots.length > 1) {
    return `Les spots ${spots.slice(0, -1).join(', ')} et ${spots[spots.length - 1]} sont des doubles bancs, sans accès direct aux sacs.`;
  }
  return null;
}

/** Nom accessible d'un spot (lecteurs d'écran ; la maquette le met aussi en title). */
export function spotLabel(cell: PlanCell): string {
  const shape =
    cell.shape === 'bag' ? ', sac' : cell.shape === 'bench' ? ', banc' : cell.shape === 'double' ? ', banc double sans accès direct au sac' : '';
  const base = `Spot ${cell.spotNumber}${shape}`;
  switch (cell.state) {
    case 'mine':
      return `${base}\u00a0: ${cell.isGuestReservation ? 'réservé pour votre invité' : 'votre réservation'}`;
    case 'selected':
      return `${base}\u00a0: votre spot`;
    case 'guest':
      return `${base}\u00a0: spot de votre invité`;
    case 'taken':
      return `${base}\u00a0: pris`;
    default:
      return `${base}\u00a0: libre`;
  }
}

/* ------------------------------------------------------------ plan qui défile (téléphone) */

/** Largeur du fondu des bords du plan qui défile (class.css, .mmv2-room-scroll[data-more]). */
export const EDGE_FADE_PX = 28;

/**
 * Plan qui défile : position qui centre le spot donné (ou le milieu de la salle) ; null si le plan tient en entier.
 * `onlyIfHidden` : rien si le spot est déjà entièrement visible hors des fondus (pas de saut sous le doigt).
 */
export function centeredScrollLeft(
  box: { scrollLeft: number; scrollWidth: number; clientWidth: number; left: number },
  spot: { left: number; width: number } | null,
  onlyIfHidden = false,
): number | null {
  const max = box.scrollWidth - box.clientWidth;
  if (max <= 1) return null;
  if (spot && onlyIfHidden) {
    const start = spot.left - box.left;
    if (start >= EDGE_FADE_PX && start + spot.width <= box.clientWidth - EDGE_FADE_PX) return null;
  }
  const center = spot ? box.scrollLeft + (spot.left - box.left) + spot.width / 2 : box.scrollWidth / 2;
  return Math.round(Math.min(max, Math.max(0, center - box.clientWidth / 2)));
}
